import React, { useState, useEffect, useMemo, useRef, CSSProperties } from 'react';
import PropTypes from 'prop-types';
import { motion, AnimatePresence } from 'framer-motion';
import { Box } from '@saladbob/sassafras'


type Props = {
    children: React.ReactNode,
    button: React.ReactNode,
    style?: React.CSSProperties,
    className?: string,
    alignment?: 'tl' | 'tr' | 'tc' | 'bl' | 'br' | 'bc',
    bgColor?: string,
    depth?: 'sm' | 'md' | 'lg',
    rounded?: string,
};

const styles = {
    container: {
        position: 'relative',
        userSelect: 'none',
        cursor: 'pointer',
    },
    dropDown: {
        position: 'absolute',
        userSelect: 'none',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        transition: 'all 0.3s ease',
        zIndex: 1,
    },
    button: {
        userSelect: 'none',
        cursor: 'pointer',
    }
} as {
    container: CSSProperties,
    dropDown: CSSProperties,
    button: CSSProperties,
};

const positions = {
    bl: {
        top: '100%',
        left: 0,
    },
    br: {
        top: '100%',
        right: 0,
    },
    bc: {
        top: '100%',
        left: '50%',
        transform: 'translateX(-50%)',
    },
    tl: {
        bottom: '100%',
        right: 0,
    },
    tr: {
        bottom: '100%',
        left: 0,
    },
    tc: {
        bottom: '100%',
        left: '50%',
        transform: 'translateX(-50%)',
    }
} as {
    bl: CSSProperties,
    br: CSSProperties,
    bc: CSSProperties,
    tl: CSSProperties,
    tr: CSSProperties,
    tc: CSSProperties,
};

const variants = {
    enter: {
        y: 0,
        opacity: 1,
    },
    exit: (direction) => {
      return {
        y: direction > 0 ? -16 : 16,
        opacity: 0
      };
    }
};

const detectOutsideEdge = (element) => {
    if (!element) {
        return {};
    }

    const bounding = element.getBoundingClientRect();

    return {
        top: bounding.top < 0,
        right: bounding.right > (window.innerWidth || document.documentElement.clientWidth),
        bottom: bounding.bottom > (window.innerHeight || document.documentElement.clientHeight),
        left: bounding.left < 0,
    }
};

const DropDown: React.FC<Props> = ({ children, button, style, className, alignment, bgColor, rounded = 'sm', ...props }) => {
    const defaultPosition = useMemo(() => positions[alignment], [alignment]);
    const [open, setOpen] = useState(false);
    const [position, setPosition] = useState<CSSProperties>({
        top: '100%',
        left: 0,
    });
    const direction = alignment.includes('t') ? -1 : 1;
    const dropDown = useRef(null);
    const originClick = useRef(false);



    useEffect(() => {
        if (open) {
            const newPos = { ...defaultPosition };
            const isOutside = detectOutsideEdge(dropDown.current);

            if (isOutside.right) {
                newPos.left = null;
                newPos.right = 0;
                newPos.transform = 'translateX(0)';
            }
            if (isOutside.bottom) {
                newPos.top = null;
                newPos.bottom = '100%';
                newPos.transform = 'translateX(0)';
            }
            if (isOutside.left) {
                newPos.left = 0;
                newPos.right = null;
                newPos.transform = 'translateX(0)';
            }
            if (isOutside.top) {
                newPos.bottom = null;
                newPos.top = '100%';
                newPos.transform = 'translateX(0)';
            }

            setPosition(newPos);
        }
    }, [open]);

    useEffect(() => {
        const docClick = () => {
            if (!originClick.current) {
                setOpen(false)
            } else {
                originClick.current = false;
            }
        };
        window.document.addEventListener('click', docClick);

        return () => {
            window.document.removeEventListener('click', docClick);
        }
    }, []);

    return (
        <Box className={className} style={{ ...styles.container, ...style }} display="inline-block" width="auto">
            <div role="button" onClick={toggle} style={styles.button}>
                {button}
            </div>
            <AnimatePresence custom={direction}>
                {open && (
                    <div
                        ref={dropDown}
                        style={{ ...styles.dropDown, ...position, zIndex: 1 }}
                    >
                        <motion.div
                            key="modal"
                            custom={direction}
                            variants={variants}
                            initial="exit"
                            animate="enter"
                            exit="exit"
                            transition={{ duration: 0.2 }}
                        >
                            <Box
                                rounded={rounded}
                                bgColor={bgColor}
                                mt="xxs"
                                mb="xxs"
                                overflow="hidden"
                                {...props}
                            >
                                {children}
                            </Box>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </Box>
    );

    function toggle() {
        originClick.current = true;
        setPosition(defaultPosition);
        setOpen(!open);
    }
}

DropDown.propTypes = {
    alignment: PropTypes.oneOf(['tl', 'tr', 'tc', 'bl', 'br', 'bc']),
    bgColor: PropTypes.string,
}

DropDown.defaultProps = {
    alignment: 'tl',
    bgColor: 'white',
}

export default DropDown;
